import { Box, Container, Flex, Heading, Text } from "@radix-ui/themes";
import { Helmet } from "react-helmet-async";
import { TitleBlock } from "../TitleBlock";
import { siteUrl } from "../../lib/site";

export function SafetyProtocolPage() {
  return (
    <Container size="4" className="route-page">
      <Helmet>
        <title>Safety Protocol | Iris</title>
        <meta
          name="description"
          content="IrisChat's commitment to user safety, crisis response, and mental health support protocols."
        />
        <link rel="canonical" href={siteUrl("/safety-protocol/")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl("/safety-protocol/")} />
        <meta property="og:title" content="Safety Protocol | Iris" />
        <meta
          property="og:description"
          content="IrisChat's commitment to user safety, crisis response, and mental health support protocols."
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Safety Protocol | Iris" />
        <meta
          name="twitter:description"
          content="IrisChat's commitment to user safety, crisis response, and mental health support protocols."
        />
      </Helmet>
      <Flex direction="column" gap="6">
        <TitleBlock title="Safety Protocol" />

        {/* Our Commitment to User Safety */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Our Commitment to User Safety</Heading>
            <Text size="3" className="route-body">
              IrisChat is designed to support mental health and emotional
              wellbeing. Because users may sometimes share content related to
              distress, self-harm, or crisis situations, IrisChat maintains the
              following safety protocols in accordance with applicable law and
              our commitment to user wellbeing.
            </Text>
          </Flex>
        </Box>

        {/* Crisis Detection and Referral */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Crisis Detection and Referral</Heading>
            <Text size="3" className="route-body">
              IrisChat includes automated safety features designed to detect
              certain types of crisis-related content in user conversations,
              including expressions of suicidal ideation, self-harm, or
              statements indicating a risk to the user's safety or the safety of
              others.
            </Text>
            <Text size="3" className="route-body">
              When such content is detected, IrisChat is designed to display a
              notification directing the user to relevant crisis service
              providers, which may include:
            </Text>
            <ul className="route-list">
              <li>
                <Text size="3" className="route-body">
                  <strong>988 Suicide &amp; Crisis Lifeline</strong> — call or
                  text <strong>988</strong>
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  <strong>Crisis Text Line</strong> — text HOME to{" "}
                  <strong>741741</strong>
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  <strong>Emergency services</strong> — call{" "}
                  <strong>911</strong>
                </Text>
              </li>
            </ul>
          </Flex>
        </Box>

        {/* Limitations of Automated Detection */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Limitations of Automated Detection</Heading>
            <Text size="3" className="route-body">
              IrisChat's crisis detection is performed by automated systems.
              These systems are{" "}
              <strong>
                not guaranteed to identify all situations that may require
                intervention
              </strong>
              . The classifier may fail to detect crisis content in some cases.
            </Text>
            <Text size="3" className="route-body">
              <strong>
                You should not rely on IrisChat to recognize, detect, or respond
                to a mental health crisis or medical emergency.
              </strong>
            </Text>
            <Text size="3" className="route-body">
              If you or someone you know is experiencing a mental health crisis,
              suicidal thoughts, or a medical emergency, please contact
              emergency services (911) or the 988 Suicide &amp; Crisis Lifeline
              (call or text 988) immediately. Do not use IrisChat to report an
              emergency.
            </Text>
          </Flex>
        </Box>

        {/* Prohibited Content */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Prohibited Content</Heading>
            <Text size="3" className="route-body">
              IrisChat maintains protocols to prevent the generation of content
              that promotes, encourages, or provides instructions for suicide or
              self-harm. These protocols are applied across all user
              interactions.
            </Text>
          </Flex>
        </Box>

        {/* Protocol Review */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Protocol Review</Heading>
            <Text size="3" className="route-body">
              FL105 reviews and updates these safety protocols on an ongoing
              basis. This page reflects the protocols currently in effect.
            </Text>
            <Text size="3" className="route-body">
              For questions about IrisChat's safety practices, contact us at{" "}
              <a href="mailto:privacy@irischat.ai" className="route-link">
                privacy@irischat.ai
              </a>
              .
            </Text>
          </Flex>
        </Box>

        {/* Related Resources */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Related Resources</Heading>
            <ul className="route-list">
              <li>
                <Text size="3" className="route-body">
                  <a href="/privacy" className="route-link">
                    IrisChat Privacy Policy
                  </a>
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  <a href="/terms-conditions" className="route-link">
                    IrisChat Terms of Service
                  </a>
                </Text>
              </li>
            </ul>
          </Flex>
        </Box>
      </Flex>
    </Container>
  );
}
